import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Stack, TextField, Chip, IconButton, Tooltip, Typography, InputAdornment, Button, FormControlLabel } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Search, Refresh, Add, CheckCircle, Cancel, Delete, Edit } from '@mui/icons-material';
import MainText from '../../components/MainText';
import CustomSwitch from '../../components/CustomSwitch';
import CleanningButton from '../../components/CleanningButton';
import CustomModal from '../../components/CustomModal';
import { RBAC } from '../../services/api';
import { useDispatch } from 'react-redux';
import { addTab } from '../../tabs/tabsSlice';
import { useAuth } from '../../auth/AuthProvider';
import CustomSnackBar from '../../components/CustomSnackBar';

const getErrorMessage = (err, fallback) => {
  if (!err?.response) {
    const msg = err?.message || '';
    if (typeof msg === 'string' && msg.toLowerCase().includes('network')) {
      return 'Error de red. Verifica tu conexion.';
    }
    if (err?.code === 'ERR_NETWORK') {
      return 'Error de red. Verifica tu conexion.';
    }
    return fallback;
  }

  const data = err?.response?.data;
  if (typeof data === 'string') {
    const isHtml = /<\s*!doctype|<\s*html|<\s*body|<\s*table|<\s*pre/i.test(data);
    if (isHtml) {
      if (err?.response?.status === 413) {
        return 'El archivo es demasiado grande (413)';
      }
      return 'Error interno del servidor. Revisa los logs.';
    }
    return data;
  }
  if (!data) return fallback;
  if (data.detail) return data.detail;
  if (data.message) {
    if (typeof data.message === 'string') return data.message;
    if (typeof data.message === 'object') {
      const key = Object.keys(data.message)[0];
      const val = data.message?.[key];
      if (Array.isArray(val)) return val.join(' ');
      if (typeof val === 'string') return val;
    }
  }
  if (data.name) {
    if (Array.isArray(data.name)) return data.name.join(' ');
    if (typeof data.name === 'string') return data.name;
  }
  const key = Object.keys(data)[0];
  if (key) {
    const val = data[key];
    if (Array.isArray(val)) return val.join(' ');
    if (typeof val === 'string') return val;
  }
  return fallback;
};


function ObjectiveTypeForm({ initial, onSubmit }) {
  const [form, setForm] = useState({ name: initial?.name || '', is_active: initial?.is_active ?? true });
  const handleSave = () => onSubmit(form);
  return (
    <Stack spacing={2} sx={{ p: 1 }}>
      <TextField size="small" label="Nombre del tipo de objetivo" value={form.name} onChange={e => setForm(v => ({ ...v, name: e.target.value }))} />
      <FormControlLabel control={<CustomSwitch name="is_active" label={form.is_active ? 'Activo' : 'Inactivo'} initialValue={form.is_active} onChange={(_, val) => setForm(v => ({ ...v, is_active: val }))} />} label="" />
      <Box sx={{ textAlign: 'right' }}>
        <Button variant="contained" onClick={handleSave}>Guardar</Button>
      </Box>
    </Stack>
  );
}

export default function ObjectiveTypeList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ search: '', active: true });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const dispatch = useDispatch();
  const { isSuperuser, permissions } = useAuth();
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSev, setSnackSev] = useState('info');

  const canManage = useMemo(() => isSuperuser || (permissions || []).includes('manage_supplier'), [isSuperuser, permissions]);

  useEffect(() => { dispatch(addTab({ label: 'Tipos de objetivo', path: '/catalogs/objective-types' })); }, [dispatch]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search || ''), 450);
    return () => clearTimeout(t);
  }, [filters.search]);

  const handleFilter = (name, value) => setFilters(v => ({ ...v, [name]: value }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await RBAC.objectiveTypes.list();
      const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      let out = data.map(x => ({ id: x.id, ...x }));
      out = out.filter(r => (filters.active ? r.is_active : !r.is_active));
      const s = (debouncedSearch || '').toLowerCase().trim();
      if (s) out = out.filter(r => String(r.name || '').toLowerCase().includes(s));
      setRows(out);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filters.active]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (form) => {
    try {
      if (editRow) {
        await RBAC.objectiveTypes.update(editRow.id, form);
      } else {
        await RBAC.objectiveTypes.create(form);
      }
      setSnackMsg('Guardado');
      setSnackSev('success');
      setSnackOpen(true);
      setDialogOpen(false);
      setEditRow(null);
      load();
    } catch (e) {
      setSnackMsg(getErrorMessage(e, 'No se pudo guardar'));
      setSnackSev('error');
      setSnackOpen(true);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm('¿Eliminar este tipo de objetivo?')) return;
    try {
      await RBAC.objectiveTypes.remove(row.id);
      load();
    } catch (e) {
      setSnackMsg(getErrorMessage(e, 'No se pudo eliminar'));
      setSnackSev('error');
      setSnackOpen(true);
    }
  };

  const columns = [
    { field: 'name', headerName: 'Nombre', flex: 1 },
    {
      field: 'is_active',
      headerName: 'Estado',
      width: 120,
      renderCell: (params) => params.row.is_active ? <Chip size="small" color="success" label="Activo" icon={<CheckCircle />} /> : <Chip size="small" color="default" label="Inactivo" icon={<Cancel />} />,
    },
    canManage && {
      field: 'actions',
      headerName: 'Acciones',
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => { setEditRow(params.row); setDialogOpen(true); }}><Edit fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton size="small" onClick={() => handleDelete(params.row)}><Delete fontSize="small" /></IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ].filter(Boolean);

  return (
    <Box sx={{ p: 2 }}>
      <MainText title="Tipos de objetivo" subtitle="Catálogo" />
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small"
          placeholder="Buscar..."
          value={filters.search}
          onChange={e => handleFilter('search', e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search /></InputAdornment>
          }}
        />
        <CustomSwitch name="active" label="Activos" initialValue={filters.active} onChange={(_, v) => handleFilter('active', v)} />
        <CleanningButton onClick={() => setFilters({ search: '', active: true })} />
        <Tooltip title="Recargar"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
        {canManage && <Button startIcon={<Add />} variant="contained" onClick={() => { setEditRow(null); setDialogOpen(true); }}>Nuevo</Button>}
      </Stack>
      <div style={{ height: 520, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 20]}
          loading={loading}
          disableSelectionOnClick
        />
      </div>
      <CustomModal
        open={dialogOpen}
        setOpen={(v) => { setDialogOpen(v); if (!v) setEditRow(null); }}
        title={editRow ? 'Editar tipo de objetivo' : 'Crear tipo de objetivo'}
        component={ObjectiveTypeForm}
        componentProps={{ initial: editRow, onSubmit: handleSubmit }}
        width="420px"
      />
      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
    </Box>
  );
}
